import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

const SELECTED_PROJECT_KEY = 'yom_selected_project'

export interface ProjectState {
    selectedId: string | null
    /**
     * Sub-tenant every v2 report is filtered by: null = whole project,
     * '' = the project's own traffic (no sub-tenant), otherwise an external id.
     */
    subTenant: string | null
}

function readStored(): string | null {
    try {
        return globalThis.localStorage?.getItem(SELECTED_PROJECT_KEY) ?? null
    } catch {
        return null
    }
}

function writeStored(id: string | null) {
    try {
        if (id) {
            globalThis.localStorage?.setItem(SELECTED_PROJECT_KEY, id)
        } else {
            globalThis.localStorage?.removeItem(SELECTED_PROJECT_KEY)
        }
    } catch {
        // storage unavailable (private mode) — selection still lives in memory
    }
}

const initialState: ProjectState = {
    selectedId: readStored(),
    subTenant: null,
}

const projectSlice = createSlice({
    name: 'project',
    initialState,
    reducers: {
        selectProject(state, action: PayloadAction<string>) {
            if (state.selectedId !== action.payload) {
                state.subTenant = null
            }
            state.selectedId = action.payload
            writeStored(action.payload)
        },
        clearProject(state) {
            state.selectedId = null
            state.subTenant = null
            writeStored(null)
        },
        setSubTenant(state, action: PayloadAction<string | null>) {
            state.subTenant = action.payload
        },
    },
})

export const { selectProject, clearProject, setSubTenant } = projectSlice.actions
export const projectReducer = projectSlice.reducer

/**
 * The project the dashboard should show, given the projects the API returned:
 * keep the current selection when it belongs to this account, otherwise fall
 * back to the first project (or none). A selection persisted by another account
 * in the same browser is never kept.
 */
export function resolveSelectedProject(
    projects: ReadonlyArray<{ id: string }>,
    selectedId: string | null,
): string | null {
    if (selectedId && projects.some(p => p.id === selectedId)) {
        return selectedId
    }
    return projects[0]?.id ?? null
}
